import { style, styleVariants } from '@vanilla-extract/css'
import { recipe } from '@vanilla-extract/recipes'
import { vars, focusRing } from '../theme.css'

// Button — the deliberate, usable control (brief §4.1). Square idiom.
export const button = recipe({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: vars.space[2],
    fontFamily: vars.font.mono,
    fontSize: vars.ms.md,
    letterSpacing: vars.ls.label,
    textTransform: 'uppercase',
    lineHeight: 1,
    borderRadius: vars.radius.sm,
    border: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: `background ${vars.dur.fast} ease, box-shadow ${vars.dur.fast} ease, color ${vars.dur.fast} ease`,
    selectors: {
      '&:disabled': { opacity: 0.45, cursor: 'not-allowed' },
      '&:focus-visible': focusRing,
    },
  },
  variants: {
    variant: {
      primary: {
        background: vars.color.ink,
        color: vars.color.paper,
        selectors: { '&:hover:not(:disabled)': { background: vars.color.accent2 } },
      },
      ghost: {
        background: vars.color.paper,
        color: vars.color.ink,
        boxShadow: vars.shadow.edge,
        selectors: { '&:hover:not(:disabled)': { background: vars.color.paper2 } },
      },
      quiet: {
        background: 'transparent',
        color: vars.color.ink2,
        selectors: { '&:hover:not(:disabled)': { color: vars.color.ink } },
      },
      danger: {
        background: vars.color.red,
        color: vars.color.paper,
        selectors: { '&:hover:not(:disabled)': { filter: 'brightness(0.92)' } },
      },
    },
    size: {
      sm: { height: vars.ctrl.sm, paddingInline: vars.space[3], fontSize: vars.ms.sm },
      md: { height: vars.ctrl.md, paddingInline: vars.space[4] },
      lg: { height: vars.ctrl.lg, paddingInline: vars.space[5], fontSize: vars.ms.lg },
    },
    block: { true: { width: '100%' } },
  },
  defaultVariants: { variant: 'ghost', size: 'md' },
})

// SegmentedControl — THE option group, and now the only one. It used to be a chromed pill on a
// recessed track (Stats' period, the Directory's Show/Sort) while the identical control beside
// Live's sparkline was plain marked text, so the same job wore two costumes on two pages. This is
// the plain one: muted mono, the live option inked and underlined. No track, no sliding pill, no
// shadow — the page is paper, and a filter is not a switch on a machine.
export const segTrack = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: vars.space[1],
})
export const segItem = recipe({
  base: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: vars.font.mono,
    fontSize: vars.ms.sm,
    letterSpacing: vars.ls.label,
    textTransform: 'uppercase',
    color: vars.color.inkSoft,
    // A real target on touch (44px, §5) without the control LOOKING 44px tall: the box is
    // padded, the ink is not.
    minHeight: vars.ctrl.lg,
    display: 'inline-flex',
    alignItems: 'center',
    paddingInline: vars.space[2],
    borderRadius: vars.radius.none,
    lineHeight: 1,
    transition: `color ${vars.dur.fast} ease`,
    selectors: { '&:focus-visible': focusRing },
    '@media': { 'screen and (min-width: 700px)': { minHeight: vars.ctrl.sm } },
  },
  variants: {
    active: {
      true: {
        color: vars.color.ink,
        textDecoration: 'underline',
        textUnderlineOffset: vars.uline.mark,
      },
      false: { selectors: { '&:hover': { color: vars.color.ink2 } } },
    },
  },
  defaultVariants: { active: false },
})

// Field / Select — text inputs, recessed, mono value.
export const field = style({
  fontFamily: vars.font.mono,
  fontSize: vars.ms.md,
  color: vars.color.ink,
  height: vars.ctrl.md,
  paddingInline: vars.space[3],
  width: '100%',
  background: vars.color.paper,
  border: 'none',
  borderRadius: vars.radius.sm,
  boxShadow: vars.shadow.recess,
  selectors: {
    '&::placeholder': { color: vars.color.inkSoft },
    '&:focus-visible': focusRing,
  },
})
export const textarea = style([
  field,
  {
    height: 'auto',
    minHeight: vars.ctrl.lg,
    paddingBlock: vars.space[2],
    lineHeight: vars.lh.snug,
    resize: 'vertical',
  },
])

// Checkbox — a square mark, ink when checked.
//
// The unchecked box wore `--edge`, whose outer hairline is black at 4% — on white paper that is
// no line at all, so the one toggle in the account drawer was an invisible 18px of nothing. It
// takes the publication's own hairline instead, the same rule every ruled row beside it uses.
//
// The TARGET is 44px and the MARK stays 18 (§5): a transparent ::before spreads the hit area
// past the ink, the way the nav glyphs and the EN|GA pair grow their boxes without growing.
export const checkbox = styleVariants({
  input: {
    appearance: 'none',
    position: 'relative',
    width: vars.mark.iconLg,
    height: vars.mark.iconLg,
    flex: 'none',
    borderRadius: vars.radius.sm,
    border: `1px solid ${vars.color.rule}`,
    background: vars.color.paper,
    cursor: 'pointer',
    display: 'inline-grid',
    placeContent: 'center',
    transition: `border-color ${vars.dur.fast} ease, background ${vars.dur.fast} ease`,
    selectors: {
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: vars.ctrl.lg,
        height: vars.ctrl.lg,
        transform: 'translate(-50%, -50%)',
      },
      '&:hover': { borderColor: vars.color.inkSoft },
      '&:checked': { background: vars.color.ink, borderColor: vars.color.ink },
      '&:checked::after': { content: '""', width: '9px', height: '9px', background: vars.color.paper },
      '&:focus-visible': focusRing,
    },
  },
})

// THE close mark — the species sheet, the account drawer and the admin workspace each had their
// own, differing only in which grey they hovered to. A real 44px target on touch.
export const close = style({
  flex: 'none',
  appearance: 'none',
  background: 'none',
  border: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: vars.ctrl.lg,
  minHeight: vars.ctrl.lg,
  padding: 0,
  fontFamily: vars.font.mono,
  fontSize: vars.fs.lg,
  lineHeight: 1,
  color: vars.color.ink2,
  transition: `color ${vars.dur.fast} ease`,
  selectors: { '&:hover': { color: vars.color.ink }, '&:focus-visible': focusRing },
})

// A quiet mono control that is just text — the Journal's "show more", Admin's "back", the
// species sheet's "look up folklore". They were four near-identical hand-rolled resets.
export const quietText = style({
  appearance: 'none',
  background: 'transparent',
  border: 0,
  padding: 0,
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: vars.font.mono,
  fontSize: vars.ms.sm,
  letterSpacing: vars.ls.label,
  textTransform: 'uppercase',
  color: vars.color.inkSoft,
  transition: `color ${vars.dur.fast} ease`,
  selectors: {
    '&:hover:not(:disabled)': { color: vars.color.ink },
    '&:disabled': { cursor: 'default', opacity: 0.5 },
    '&:focus-visible': focusRing,
  },
})
